import { AxiosResponse } from 'axios';

import AbstractSiteApi from '../AbstractSiteApi';
import GetDepositResponse from '../CRM/Responses/DepositResponse';
import GetPaginatedResponse from '../CRM/Responses/PaginatedResponse';
import GetWithdrawalResponse from '../CRM/Responses/WithdrawalResponse';
import DepositCalculatedAmounts from '../Models/DepositCalculatedAmounts';
import PaymentMethodChannel from '../Models/PaymentMethodChannel';
import ResellerCustomer from '../Models/ResellerClient';
import { ResourcePaginatedResponse } from '../Models/SiteApi/ResourcePaginatedResponse';
import SiteLotteryTicket from '../Models/SiteApi/SiteLotteryTicket';
import { Ticket, TicketsRequestTypes } from '../Models/SiteApi/Ticket';
import UserAccountChange from '../Models/UserAccountChange';
import Voucher, { VoucherStatus, VoucherStatuses } from '../Models/Voucher';

import {
    AccountApiGetDepositBoxValuesAPIResponse,
    AccountApiGetDepositBoxValuesResponse,
    AccountApiGetPaymentMethodsResponse,
    AccountApiGetDetailsResponse,
    LootboxTicketResponse,
    PopTrackingEventsResponse,
    MarketingPreferencesData,
} from './Response/AccountApi';
import {
    DepositResellerResponse,
    DepositAmountsCalculationsResponse,
    PurchasedVouchersResponse,
    BankAccountsResponse,
    BankAccount,
    Transfer,
    AccountBonusesResponse,
} from './Response/SiteApi';
import { CombinedTransactions } from './Responses';

import CustomDepositBoxValue from 'Api/Models/CustomDepositBoxValue';
import DepositBoxValue from 'Api/Models/DepositBoxValue';
import PaymentCountry from 'Api/Models/PaymentCountry';
import PaymentMethod from 'Api/Models/PaymentMethod';
import UserDetails from 'Api/Models/UserDetails';

class AccountApi extends AbstractSiteApi {
    protected endpoint: string = 'account';

    async getResellerAvailableDebit(): Promise<number> {
        const response = await this.get('/reseller/available_debit');

        return response?.data?.available_debit ?? 0;
    }

    async getDepositReseller(accountName: string, term?: string): Promise<DepositResellerResponse> {
        const query = new URLSearchParams({ term }).toString();
        return (await this.get(`/deposit/reseller-bank-account/${accountName}?${query}`)).data;
    }

    async getTicketSingle(token: string): Promise<SiteLotteryTicket> {
        const response = await this.get('/tickets/single', {
            params: {
                token,
            },
        });

        return response.data;
    }

    public getDepositBoxValues(): Promise<AccountApiGetDepositBoxValuesResponse> {
        return this.get('/deposit_box_values').then(
            (response: AxiosResponse<AccountApiGetDepositBoxValuesAPIResponse>) => {
                const data: AccountApiGetDepositBoxValuesResponse = {
                    customFieldValue: CustomDepositBoxValue.create(response.data.customFieldValue || 0),
                    depositBoxValues: [],
                };

                response.data.depositBoxValues.forEach((item) => {
                    data.depositBoxValues.push(AbstractSiteApi.mapDataToModel(item, DepositBoxValue));
                });
                return data;
            }
        );
    }

    public getPaymentMethods(): Promise<AccountApiGetPaymentMethodsResponse> {
        return this.get('/payment_methods').then(
            (response): AccountApiGetPaymentMethodsResponse => {
                return response.data.map((item) => {
                    const data = AbstractSiteApi.mapDataToModel(item, PaymentMethod);
                    data.paymentCountries = data.paymentCountries.map((i) =>
                        AbstractSiteApi.mapDataToModel(i, PaymentCountry)
                    );
                    if (item.channels) {
                        data.channels = item.channels.map((channel) =>
                            AbstractSiteApi.mapDataToModel(channel, PaymentMethodChannel)
                        );
                    }
                    return data;
                });
            }
        );
    }

    public calculateDepositAmountsFromSum(
        paymentMethod: PaymentMethod,
        amount: number
    ): Promise<DepositAmountsCalculationsResponse> {
        return this.get('/deposit_amounts/from_sum', {
            params: {
                payment_method: paymentMethod.id,
                amount,
            },
        }).then(
            (response): DepositAmountsCalculationsResponse => {
                return {
                    user: AbstractSiteApi.mapDataToModel(response.data.user, DepositCalculatedAmounts),
                    transaction: AbstractSiteApi.mapDataToModel(response.data.transaction, DepositCalculatedAmounts),
                };
            }
        );
    }

    public calculateDepositAmountsFromAmount(
        paymentMethod: PaymentMethod,
        amount: number
    ): Promise<DepositAmountsCalculationsResponse> {
        return this.get('/deposit_amounts/from_amount', {
            params: {
                payment_method: paymentMethod.id,
                amount,
            },
        }).then(
            (response): DepositAmountsCalculationsResponse => {
                return {
                    user: AbstractSiteApi.mapDataToModel(response.data.user, DepositCalculatedAmounts),
                    transaction: AbstractSiteApi.mapDataToModel(response.data.transaction, DepositCalculatedAmounts),
                };
            }
        );
    }

    public getUserDetails(): Promise<UserDetails> {
        return this.get('/details').then((response: AxiosResponse<AccountApiGetDetailsResponse>) => {
            return AbstractSiteApi.mapDataToModel(response.data.details, UserDetails);
        });
    }

    public async getDetails(): Promise<AccountApiGetDetailsResponse> {
        return (await this.get('/details')).data;
    }

    public async getAllTicketsPaginated(
        type: string = TicketsRequestTypes.ALL,
        page: number = 1,
        perPage: number = 1
    ): Promise<GetPaginatedResponse<Ticket>> {
        const response = await this.get('/tickets', {
            params: {
                type,
                perPage,
                page,
            },
        });
        return response.data.tickets;
    }

    public async getAllTransactionsPaginated(
        page: number = 1,
        perPage: number = 1
    ): Promise<GetPaginatedResponse<CombinedTransactions>> {
        const response = await this.get('/transactions/all', {
            params: {
                perPage,
                page,
            },
        });
        return response.data.transactions;
    }

    async getPurchasedVoucherList(
        status: VoucherStatus = VoucherStatuses.ALL,
        page: number = 1
    ): Promise<PurchasedVouchersResponse> {
        const params = status !== VoucherStatuses.ALL ? { status, page } : { page };
        const response = await this.get('/reseller/vouchers', { params });

        return {
            data: response.data.vouchers.data,
            usedVouchersCount: response.data.usedVouchersCount,
            notUsedVouchersCount: response.data.notUsedVouchersCount,
            currentPage: response.data.vouchers.current_page,
            lastPage: response.data.vouchers.last_page,
        };
    }

    async getUsedVouchersList(page: number = 1): Promise<PurchasedVouchersResponse> {
        const params = { page };
        const response = await this.get('/vouchers', { params });

        return {
            data: response.data.vouchers.data,
            usedVouchersCount: response.data.usedVouchersCount,
            notUsedVouchersCount: response.data.notUsedVouchersCount,
            currentPage: response.data.vouchers.current_page,
            lastPage: response.data.vouchers.last_page,
        };
    }

    async addCustomer(formCustomer: ResellerCustomer): Promise<ResellerCustomer> {
        const response: AxiosResponse<ResellerCustomer> = await this.post('/customers/add', {
            ...formCustomer,
        });
        return response.data;
    }

    async editCustomer(formCustomer: ResellerCustomer, customerId: number): Promise<ResellerCustomer> {
        const response: AxiosResponse<ResellerCustomer> = await this.post(`/customers/edit/${customerId}`, {
            ...formCustomer,
        });
        return response.data;
    }

    async getCustomerById(userId: number): Promise<ResellerCustomer> {
        const response = await this.get('/customers/single', {
            params: { id: userId },
        });
        return response.data.customer;
    }

    async purchaseVoucher(amount: string): Promise<Voucher> {
        const response = await this.post('/vouchers/purchase', { amount });
        return response.data;
    }

    async useVoucher(voucherCode: string): Promise<Voucher> {
        const response = await this.post('/vouchers/use', { voucher_code: voucherCode });
        return response.data;
    }

    async printVoucher(voucherId: number): Promise<AxiosResponse> {
        return this.get(`/vouchers/${voucherId}/print`);
    }

    public async getBankAccounts(): Promise<BankAccountsResponse> {
        const response: AxiosResponse<BankAccountsResponse> = await this.get('/bank-accounts');
        return response.data;
    }

    public async addBankAccount(accountName: string, accountNumber: string): Promise<BankAccount> {
        const response = await this.post('/bank-accounts/add', {
            account_name: accountName,
            account_number: accountNumber,
        });
        return response.data;
    }

    public async getTransfers(): Promise<Transfer[]> {
        const response = await this.get('/transfers');
        return response.data?.data ?? response.data ?? [];
    }

    public async addTransfer(
        amount: string,
        target: 'account_by_email' | 'account_by_phone',
        targetEmail?: string,
        targetPhone?: string
    ): Promise<Transfer> {
        const response = await this.post('/transfers', {
            amount,
            target,
            target_email: targetEmail,
            target_phone: targetPhone,
        });
        return response.data?.data ?? response.data;
    }

    public async deleteBankAccount(accountId: number): Promise<void> {
        await this.post(`/bank-accounts/delete/${accountId}`);
    }

    public async getDeposit(depositToken: string): Promise<AxiosResponse<GetDepositResponse>> {
        const response = await this.get('/transactions/deposit', {
            params: {
                token: depositToken,
            },
        });

        return response;
    }

    public async getWithdrawal(withdrawalToken: string): Promise<GetWithdrawalResponse> {
        return (await this.get(`/transactions/withdrawal/${withdrawalToken}`)).data;
    }

    public async getUserAccountChangesPaginated(
        page: number = 1,
        perPage: number = 20
    ): Promise<ResourcePaginatedResponse<UserAccountChange>> {
        const response = await this.get('/user-account-changes', {
            params: {
                perPage,
                page,
            },
        });

        return {
            ...response.data,
            data: response.data.data.map((item) => AbstractSiteApi.mapDataToModel(item, UserAccountChange)),
        };
    }

    public async getBonuses(): Promise<AccountBonusesResponse> {
        return (await this.get('/bonuses')).data;
    }

    public async popTrackingEvents(): Promise<PopTrackingEventsResponse> {
        const params = { time: Date.now() };
        return (await this.get('/tracking_events', { params })).data;
    }

    public async getLootboxTicket(token: string): Promise<LootboxTicketResponse> {
        return (await this.get('/lootboxes/single', { params: { token } })).data;
    }

    public async getMarketingPreferences(): Promise<MarketingPreferencesData> {
        return (await this.get('/marketing-preferences')).data;
    }

    public async updateMarketingPreferences(type: 'email' | 'sms', value: boolean): Promise<MarketingPreferencesData> {
        return (await this.post('/marketing-preferences', { type, value })).data;
    }
}

export default new AccountApi();
